'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var yogokit = require('@wishket/yogokit');
var AutoCompleteList = require('../AutoCompleteList/AutoCompleteList.js');
var TextFieldContainer = require('../TextFieldContainer/TextFieldContainer.js');
require('../../../_virtual/_tslib.js');
require('tailwind-merge');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
var Input = require('../Input/Input.js');
require('../Input/PasswordInput.js');
require('../Input/LableInput.js');
require('../Input/InputTypeSelector.js');
var InputChip = require('../InputChip/InputChip.js');
var SupportTextContainer = require('../SupportTextContainer/SupportTextContainer.js');
var Autocomplete_parts = require('./Autocomplete.parts.js');
var Autocomplete_utils = require('./Autocomplete.utils.js');

/**
 * 자동완성 입력 컴포넌트
 *
 * @param {AutocompleteProps} props
 * @param {SkillItem[]} props.skillItems - 선택 가능한 기술 항목 목록
 * @param {LegacySkillItem[]} props.legacySkillItems - 레거시 기술 항목 목록
 * @param {string[]} props.selectedItems - 현재 선택된 항목들
 * @param {(item: string) => void} props.onChipDelete - 선택된 항목 삭제 시 호출되는 콜백
 * @param {string} props.value - 입력 필드의 현재 값
 * @param {(value: string) => void} props.onValueChange - 입력 값 변경 시 호출되는 콜백
 * @param {string} props.placeholder - 입력 필드의 플레이스홀더
 * @param {(item: SkillItem) => void} props.onSkillItemClick - 기술 항목 선택 시 호출되는 콜백
 * @param {(item: LegacySkillItem) => void} props.onLegacySkillItemClick - 레거시 항목 선택 시 호출되는 콜백
 * @param {(value: string) => void} props.onInputItemClick - 직접 입력한 값 선택 시 호출되는 콜백
 * @param {string} [props.supportingText] - 입력 필드 아래에 표시되는 도움말 텍스트
 * @param {boolean} [props.hasIndexBar=false] - 알파벳 인덱스 바 표시 여부
 * @param {boolean} [props.isError] - 오류 상태 여부
 * @param {string} [props.errorMessage] - 오류 메시지
 * @param {ReactNode} [props.leadingIcon] - 항목 앞에 표시될 아이콘
 *
 * @example
 * ```tsx
 * <Autocomplete
 *   skillItems={[
 *     { value: 'JavaScript', iconUrl: '/js-icon.png' },
 *     { value: 'TypeScript', iconUrl: '/ts-icon.png' }
 *   ]}
 *   legacySkillItems={[
 *     { value: 'jQuery', count: 5 }
 *   ]}
 *   selectedItems={['React']}
 *   value=""
 *   onValueChange={(value) => console.log(value)}
 *   onChipDelete={(item) => console.log('deleted:', item)}
 *   onSkillItemClick={(item) => console.log('selected:', item)}
 *   placeholder="기술을 입력하세요"
 * />
 * ```
 */ const Autocomplete = ({ skillItems, legacySkillItems, selectedItems, onChipDelete, value, onValueChange, placeholder, onSkillItemClick, onLegacySkillItemClick, onInputItemClick, supportingText, hasIndexBar = false, isError, errorMessage, leadingIcon })=>{
    const [isListOpen, setIsListOpen] = react.useState(false);
    const rootRef = react.useRef(null);
    const labelRef = react.useRef(null);
    const listRef = react.useRef(null);
    const itemListRef = react.useRef(null);
    const [labelHeight, setLabelHeight] = react.useState(54);
    const [recentlyAdded, setRecentlyAdded] = react.useState([]);
    const [focusedIndex, setFocusedIndex] = react.useState(-1);
    const sortedSkillItems = Autocomplete_utils.getSortedSkillItems(skillItems);
    const sortedLegacySkillItems = Autocomplete_utils.getSortedSkillItems(legacySkillItems);
    const closeList = ()=>{
        setIsListOpen(false);
    };
    react.useEffect(()=>{
        if (!labelRef.current) return;
        const observer = new ResizeObserver((entries)=>{
            for (const entry of entries){
                if (entry.target === labelRef.current) {
                    setLabelHeight(entry.contentRect.height + 4);
                }
            }
        });
        observer.observe(labelRef.current);
        return ()=>{
            observer.disconnect();
        };
    }, []);
    yogokit.useClickOutside(rootRef, closeList);
    const handleAlphabetClick = (letter)=>{
        const targetItem = sortedSkillItems.find((item)=>item.value.toUpperCase().startsWith(letter));
        if (targetItem && listRef.current) {
            const element = listRef.current.querySelector(`[id="item-${targetItem.value.replace(/ /g, '_')}"]`);
            if (element) {
                element.scrollIntoView();
            }
        }
    };
    const allItems = [
        ...sortedSkillItems.map((item)=>({
                type: 'static_skill',
                item
            })),
        ...sortedLegacySkillItems.map((item)=>({
                type: 'legacy',
                item
            })),
        ...value ? [
            {
                type: 'input',
                item: value
            }
        ] : []
    ];
    const handleKeyDown = (e)=>{
        if (e.nativeEvent.isComposing) return;
        if (!isListOpen) return;
        switch(e.key){
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex((prev)=>{
                    const nextIndex = prev < allItems.length - 1 ? prev + 1 : prev;
                    const listElement = itemListRef.current;
                    if (listElement) {
                        const itemElement = listElement.querySelectorAll('li')[nextIndex];
                        itemElement === null || itemElement === void 0 ? void 0 : itemElement.scrollIntoView({
                            block: 'nearest'
                        });
                    }
                    return nextIndex;
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev)=>{
                    const prevIndex = prev > 0 ? prev - 1 : prev;
                    const listElement = itemListRef.current;
                    if (listElement) {
                        const itemElement = listElement.querySelectorAll('li')[prevIndex];
                        itemElement === null || itemElement === void 0 ? void 0 : itemElement.scrollIntoView({
                            block: 'nearest'
                        });
                    }
                    return prevIndex;
                });
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < allItems.length) {
                    const { type, item } = allItems[focusedIndex];
                    if (type === 'static_skill') {
                        handleSkillItemClick(item);
                    } else if (type === 'legacy') {
                        handleLegacySkillItemClick(item);
                    } else if (type === 'input') {
                        handleInputItemClick();
                    }
                } else {
                    if (value.trim()) handleInputItemClick();
                }
                break;
            case 'Backspace':
                if (!value) {
                    e.preventDefault();
                    onChipDelete(selectedItems[selectedItems.length - 1]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                closeList();
                break;
        }
    };
    const toggleIsListOpen = ()=>{
        if (isListOpen) return;
        setIsListOpen(!isListOpen);
    };
    const handleSkillItemClick = (item)=>{
        onSkillItemClick(item);
        setRecentlyAdded([
            ...recentlyAdded,
            item
        ]);
    };
    const handleLegacySkillItemClick = (item)=>{
        onLegacySkillItemClick(item);
        setRecentlyAdded([
            ...recentlyAdded,
            item
        ]);
    };
    const handleInputItemClick = ()=>{
        if (value.trim() === '') return;
        onInputItemClick(value.trim());
        onValueChange('');
        setRecentlyAdded([
            ...recentlyAdded,
            value
        ]);
    };
    const resetRecentlyAdded = ()=>setRecentlyAdded([]);
    const handleMouseLeave = ()=>{
        if (recentlyAdded.length > 0) {
            closeList();
            resetRecentlyAdded();
        }
    };
    const handleValueChange = (e)=>{
        onValueChange(e.target.value);
    };
    return jsxRuntime.jsxs("div", {
        "data-testid": "design-system-autocomplete-root",
        className: "relative flex w-full flex-col gap-3",
        ref: rootRef,
        onKeyDown: handleKeyDown,
        children: [
            jsxRuntime.jsx("label", {
                ref: labelRef,
                children: jsxRuntime.jsxs(TextFieldContainer, {
                    isError: isError,
                    isFocused: isListOpen,
                    testId: "design-system--selectBox",
                    isAutoComplete: true,
                    children: [
                        selectedItems.map((selectedItem)=>jsxRuntime.jsx(InputChip, {
                                onDelete: ()=>onChipDelete(selectedItem),
                                children: selectedItem
                            }, selectedItem)),
                        jsxRuntime.jsx(Input, {
                            value: value,
                            onChange: handleValueChange,
                            placeholder: placeholder,
                            onFocus: toggleIsListOpen,
                            className: "w-fit flex-auto"
                        })
                    ]
                })
            }),
            (supportingText || errorMessage) && jsxRuntime.jsx(SupportTextContainer, {
                supportMessage: supportingText,
                errorMessage: errorMessage
            }),
            isListOpen && jsxRuntime.jsx(Box, {
                "data-testid": "design-system-autocomplete-list",
                ref: listRef,
                className: "absolute z-40 w-full",
                style: {
                    top: labelHeight
                },
                onMouseLeave: handleMouseLeave,
                children: jsxRuntime.jsx(AutoCompleteList.Root, {
                    hasIndexBar: hasIndexBar,
                    onIndexClick: handleAlphabetClick,
                    children: jsxRuntime.jsx("ul", {
                        ref: itemListRef,
                        children: jsxRuntime.jsxs(Autocomplete_parts.WithDivider, {
                            children: [
                                skillItems.length > 0 && jsxRuntime.jsx(jsxRuntime.Fragment, {
                                    children: sortedSkillItems.map((item, index)=>jsxRuntime.jsx(AutoCompleteList.Item, {
                                            id: `item-${item.value.replace(/ /g, '_')}`,
                                            leadingIcon: leadingIcon,
                                            iconUrl: item.iconUrl,
                                            text: item.value,
                                            onClick: ()=>handleSkillItemClick(item),
                                            isFocused: focusedIndex === index,
                                            inputValue: value
                                        }, item.value))
                                }),
                                legacySkillItems.length > 0 && jsxRuntime.jsx(jsxRuntime.Fragment, {
                                    children: sortedLegacySkillItems.map((item, index)=>jsxRuntime.jsx(AutoCompleteList.Item, {
                                            text: item.value,
                                            count: item.count,
                                            onClick: ()=>handleLegacySkillItemClick(item),
                                            isFocused: focusedIndex === sortedSkillItems.length + index,
                                            inputValue: value
                                        }, `${item.value}-${index}`))
                                }),
                                value.trim() !== '' && jsxRuntime.jsx(AutoCompleteList.Item, {
                                    text: value,
                                    isInputItem: true,
                                    onClick: ()=>handleInputItemClick(),
                                    isFocused: focusedIndex === allItems.length - 1
                                })
                            ]
                        })
                    })
                })
            })
        ]
    });
};

module.exports = Autocomplete;
